import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, Stack, TextField, Typography,
} from '@mui/material';
import {
  ConfirmationNumberOutlined as TicketIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  RemoveCircleOutline as ConsumeIcon,
} from '@mui/icons-material';
import patientProcedureTicketService, {
  ProcedureTicketBalance,
  ProcedureTicketHistoryEntry,
} from '../services/patientProcedureTicketService';

type Props = {
  patientId: string;
  unitId?: string;
  compact?: boolean;
  onSessionsChanged?: () => void;
};

const EMPTY_BALANCE: ProcedureTicketBalance = {
  available: 0,
  reserved: 0,
  consumed: 0,
  total: 0,
  allocations: [],
};

const movementLabels: Record<string, string> = {
  credit: 'Crédito',
  grant: 'Crédito',
  reservation: 'Reserva',
  reserve: 'Reserva',
  release: 'Liberação',
  consumption: 'Consumo',
  consume: 'Consumo',
  reversal: 'Estorno',
};

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('pt-BR');
};

const PatientPaidSessionsCard: React.FC<Props> = ({ patientId, unitId, compact = false, onSessionsChanged }) => {
  const [balance, setBalance] = useState<ProcedureTicketBalance>(EMPTY_BALANCE);
  const [history, setHistory] = useState<ProcedureTicketHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeQty, setConsumeQty] = useState<Record<string, string>>({});
  const [consumeNotes, setConsumeNotes] = useState('');
  const [consumeSaving, setConsumeSaving] = useState(false);
  const [consumeError, setConsumeError] = useState('');

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [nextBalance, nextHistory] = await Promise.all([
        patientProcedureTicketService.getBalance(patientId, unitId ? { unit_id: unitId } : {}),
        patientProcedureTicketService.getHistory(patientId, { limit: compact ? 5 : 10, unit_id: unitId }),
      ]);
      setBalance(nextBalance);
      setHistory(nextHistory);
      setUnavailable(false);
    } catch (error: any) {
      setBalance(EMPTY_BALANCE);
      setHistory([]);
      setUnavailable(error?.response?.status === 404 || error?.response?.status === 501);
    } finally {
      setLoading(false);
    }
  }, [compact, patientId, unitId]);

  useEffect(() => { void load(); }, [load]);

  if (unavailable) return null;

  const availableRows = balance.allocations.filter((a) => a.available_sessions > 0);
  const canConsume = balance.available > 0 && availableRows.length > 0;

  const openConsumeDialog = () => {
    const initial: Record<string, string> = {};
    availableRows.forEach((row) => {
      const tid = String(row.ticket_id || row.id);
      initial[tid] = '1';
    });
    setConsumeQty(initial);
    setConsumeNotes('');
    setConsumeError('');
    setConsumeOpen(true);
  };

  const submitConsume = async () => {
    setConsumeSaving(true);
    setConsumeError('');
    try {
      const items = availableRows
        .map((row) => {
          const ticket_id = String(row.ticket_id || row.id);
          const quantity = Math.floor(Number(consumeQty[ticket_id]) || 0);
          return { ticket_id, quantity };
        })
        .filter((item) => item.quantity > 0);
      if (!items.length) {
        setConsumeError('Informe quantas sessões baixar em ao menos um procedimento.');
        return;
      }
      for (const item of items) {
        const row = availableRows.find((r) => String(r.ticket_id || r.id) === item.ticket_id);
        const max = row?.available_sessions ?? 0;
        if (item.quantity > max) {
          setConsumeError(`Saldo insuficiente para ${row?.procedure_name || 'procedimento'} (máx. ${max}).`);
          return;
        }
      }
      await patientProcedureTicketService.consumeManual(patientId, {
        items,
        notes: consumeNotes.trim() || undefined,
        idempotency_key: `panel-${patientId}-${Date.now()}`,
      });
      setConsumeOpen(false);
      await load();
      onSessionsChanged?.();
    } catch (err: any) {
      setConsumeError(err?.response?.data?.error || err?.message || 'Não foi possível baixar as sessões.');
    } finally {
      setConsumeSaving(false);
    }
  };

  const metrics = [
    { label: 'Disponíveis', value: balance.available, color: '#059669', bg: '#ecfdf5' },
    { label: 'Reservadas', value: balance.reserved, color: '#d97706', bg: '#fffbeb' },
    { label: 'Consumidas', value: balance.consumed, color: '#2563eb', bg: '#eff6ff' },
  ];

  return (
    <Card sx={{ borderRadius: 2, border: '1px solid #bfdbfe', bgcolor: '#fff', mb: 2 }}>
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <TicketIcon sx={{ color: '#2563eb', fontSize: compact ? 18 : 21 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', flex: 1 }}>
            Sessões pagas
          </Typography>
          {canConsume && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ConsumeIcon sx={{ fontSize: 16 }} />}
              onClick={openConsumeDialog}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', py: 0.25, mr: 0.5 }}
            >
              Baixar sessões
            </Button>
          )}
          <IconButton size="small" onClick={() => void load()} disabled={loading} aria-label="Atualizar sessões pagas">
            <RefreshIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}><CircularProgress size={20} /></Box>
        ) : (
          <>
            <Stack direction="row" spacing={0.75}>
              {metrics.map((metric) => (
                <Box key={metric.label} sx={{ flex: 1, minWidth: 0, p: compact ? 0.75 : 1, borderRadius: 1.5, bgcolor: metric.bg, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: compact ? '1rem' : '1.2rem', fontWeight: 800, color: metric.color, lineHeight: 1.2 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: metric.color, fontSize: compact ? '0.58rem' : '0.65rem', fontWeight: 700 }}>
                    {metric.label}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {balance.allocations.length > 0 && (
              <Stack spacing={0.6} sx={{ mt: 1.25 }}>
                {balance.allocations.slice(0, compact ? 3 : 6).map((allocation) => (
                  <Box key={allocation.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography variant="caption" noWrap sx={{ flex: 1, color: '#475569', fontWeight: 600 }}>
                      {allocation.procedure_name || 'Procedimento'}
                      {(allocation.stage_name || allocation.stage) ? ` · ${allocation.stage_name || allocation.stage}` : ''}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${allocation.available_sessions} livre${allocation.available_sessions === 1 ? '' : 's'}`}
                      sx={{ height: 20, fontSize: '0.62rem', bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700 }}
                    />
                  </Box>
                ))}
              </Stack>
            )}

            {history.length > 0 && (
              <>
                <Divider sx={{ mt: 1.25 }} />
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setHistoryOpen((open) => !open)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') setHistoryOpen((open) => !open);
                  }}
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', pt: 0.75 }}
                >
                  <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: '#475569' }}>Histórico de sessões</Typography>
                  {historyOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
                <Collapse in={historyOpen}>
                  <Stack spacing={0.75} sx={{ pt: 0.75 }}>
                    {history.map((entry) => (
                      <Box key={entry.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Chip
                          size="small"
                          label={movementLabels[entry.movement_type] || entry.movement_type}
                          sx={{ height: 19, fontSize: '0.58rem', fontWeight: 700 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: '#334155', fontWeight: 600 }}>
                            {entry.procedure_name || entry.source_label || 'Sessão'} · {entry.quantity}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontSize: '0.62rem' }}>
                            {formatDateTime(entry.occurred_at || entry.created_at)}
                            {entry.unit_name ? ` · ${entry.unit_name}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
              </>
            )}

            {!balance.total && !history.length && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Nenhuma sessão paga disponível para este paciente.
              </Typography>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={consumeOpen} onClose={() => !consumeSaving && setConsumeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Baixar sessões manualmente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use quando a sessão já foi realizada, sem consulta concluída na agenda. O saldo pago será reduzido agora.
          </Typography>
          {consumeError && <Alert severity="error" sx={{ mb: 2 }}>{consumeError}</Alert>}
          <Stack spacing={1.5}>
            {availableRows.map((row) => {
              const ticketId = String(row.ticket_id || row.id);
              return (
                <Box
                  key={ticketId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    p: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 140 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.procedure_name || 'Procedimento'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.available_sessions} sessão(ões) disponível(is)
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Baixar"
                    inputProps={{ min: 0, max: row.available_sessions, step: 1 }}
                    value={consumeQty[ticketId] ?? '0'}
                    onChange={(e) => setConsumeQty((prev) => ({ ...prev, [ticketId]: e.target.value }))}
                    sx={{ width: 100 }}
                  />
                </Box>
              );
            })}
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Observação (opcional)"
            value={consumeNotes}
            onChange={(e) => setConsumeNotes(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConsumeOpen(false)} disabled={consumeSaving}>Cancelar</Button>
          <Button variant="contained" onClick={() => void submitConsume()} disabled={consumeSaving}>
            {consumeSaving ? 'Salvando…' : 'Confirmar baixa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PatientPaidSessionsCard;
